import React, { useState, useCallback, FormEvent } from 'react';
import { Calculator as CalculatorIcon, MessageSquare, Warehouse, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { getUser } from '../state/authSlice';
import { getIsCustomsCalculatorEnabled } from '../state/courierSlice';
import { InputField, Button, TariffCodePicker, CustomsBreakdown } from '@shared/components';
import type { TariffCode } from '@shared/sdk-v1';
import { MailBankApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../shared/api-config';
import { useCalculator } from '../hooks/useCalculator';
import { useCustomsEstimate } from '../hooks/useCustomsEstimate';
import { useAppDarkMode } from '../hooks/useAppDarkMode';
import { formatDualCurrency } from '../utils/currencyUtils';
import { useTrackEvent } from '../utils/track';

const Calculator = () => {
  const { darkMode } = useAppDarkMode();
  const track = useTrackEvent();
  const {
    state,
    settings,
    courier,
    isBarrelType,
    isWeightRequired,
    areDimensionsRequired,
    isWarehouseMode,
    warehousePlan,
    warehousePlanLoading,
    warehouseMaxWeight,
    updateField,
    handleCalculate,
    isFormValid,
  } = useCalculator();

  // Dual JMD<->USD display only applies to JMD/USD couriers (single exchangeRate).
  const currency = settings?.currency ?? (settings?.useJmd ? 'JMD' : 'USD');
  const showDual =
    settings?.showDualCurrency &&
    settings?.exchangeRate &&
    (currency === 'JMD' || currency === 'USD');
  const formatPrice = useCallback((value: string): string => {
    if (!showDual) return value;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return formatDualCurrency(num, !!settings?.useJmd, settings?.exchangeRate || 1);
  }, [showDual, settings?.useJmd, settings?.exchangeRate]);

  // Custom quote request state
  const [showCustomQuoteForm, setShowCustomQuoteForm] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useSelector(getUser);

  // Tariff-code customs estimate (CourierSettings.isCustomsCalculatorEnabled)
  const isCustomsCalculatorEnabled = useSelector(getIsCustomsCalculatorEnabled);
  const customs = useCustomsEstimate(courier?.id, isCustomsCalculatorEnabled);
  const [selectedTariff, setSelectedTariff] = useState<TariffCode | null>(null);
  const useTariff = isCustomsCalculatorEnabled && !isBarrelType && !isWarehouseMode;
  const formatCustomsMoney = useCallback(
    // Same USD -> local conversion as the customs field (see useCalculator).
    (usd: number) => {
      const local = (usd * (settings?.exchangeRate || 1)).toFixed(2);
      return showDual ? formatPrice(local) : `$${local}`;
    },
    [settings?.exchangeRate, showDual, formatPrice]
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const itemValueUsd = parseFloat(state.itemValue);
    if (useTariff && selectedTariff && Number.isFinite(itemValueUsd)) {
      const result = await customs.estimate(selectedTariff.tariffCode, itemValueUsd);
      // Unavailable (e.g. flag just turned off) -> the flat fixedCustomRate estimate.
      handleCalculate(result ? result.totalCustomsUsd : undefined);
    } else {
      customs.clear();
      handleCalculate();
    }
    track('calculator_used', {
      shipping_method: state.shippingMethod,
      package_type: state.packageType,
      is_express: state.isExpress,
    });
  };
  const handleCustomQuoteSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!courier?.id || !user?.firstName || !phoneNumber) return;

    setIsSubmitting(true);
    try {
      const mailBankApi = new MailBankApi(apiConfig());
      await mailBankApi.mailBankRequestCallback({
        realm: courier.realm,
        phoneNumber: phoneNumber,
        name: `${user.firstName} ${user.lastName} - (${user.email})`
      })

      track('custom_quote_requested', { shipping_method: state.shippingMethod });
      setCallbackSubmitted(true);
      // Reset form after submission
      setTimeout(() => {
        setShowCustomQuoteForm(false);
        setPhoneNumber('');
        setCallbackSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error requesting callback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (warehousePlanLoading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-6 pb-24 md:pb-6">
        {/* Warehouse mode banner */}
        {isWarehouseMode && warehousePlan && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Warehouse className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                Warehouse pricing: {warehousePlan.name}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                Estimates based on your warehouse plan fees (warehousing + freight).
              </p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {!isWarehouseMode && (
                <div>
                  <InputField
                    darkMode={darkMode}
                    type="select"
                    name="packageType"
                    label="Package Type"
                    value={state.packageType}
                    onChange={e => {
                      updateField('packageType', e.target.value);
                      updateField('isExpress', false);
                      updateField('isHalfPound', false);
                    }}
                    options={[
                      { value: 'Regular Package', label: 'Regular Package' },
                      ...(settings?.barrelPrice ? [{ value: 'Barrels', label: 'Barrels' }] : [])
                    ]}
                    fullWidth
                  />
                </div>
              )}
              {!isBarrelType && <>
                  {!isWarehouseMode && (
                    <div>
                      <InputField
                        darkMode={darkMode}
                        type="select"
                        name="shippingMethod"
                        label="Shipping Method"
                        value={state.shippingMethod}
                        onChange={e => updateField('shippingMethod', e.target.value)}
                        options={[
                          { value: 'Air Shipping', label: 'Air Shipping (Faster, Weight Required)' },
                          ...((settings?.seaShippingEnabled || settings?.pricePerCubicFoot) ? [{ value: 'Sea Shipping', label: 'Sea Shipping (Cheaper, Dimensions Required)' }] : [])
                        ]}
                        fullWidth
                      />
                    </div>
                  )}

                  {/* Shipping Options — hidden in warehouse mode */}
                  {!isWarehouseMode && state.shippingMethod === 'Air Shipping' && (
                    <div className="space-y-4">
                      {settings?.expressWeightPrice && settings.expressWeightPrice.length > 0 && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
                          <div className="flex items-center justify-between">
                            <InputField
                              darkMode={darkMode}
                              type="checkbox"
                              name="express"
                              label="Express Shipping"
                              value={state.isExpress}
                              onChange={(e) => {
                                const target = e.target as HTMLInputElement;
                                updateField('isExpress', target.checked);
                                updateField('weight', '');
                              }}
                            />
                            <div className="text-sm text-primary dark:text-primary">
                              Faster delivery times
                            </div>
                          </div>
                          {state.isExpress && (
                            <p className="mt-2 text-sm text-primary dark:text-primary">
                              Express shipping rates will be applied to your package
                            </p>
                          )}
                        </div>
                      )}
                      {settings?.useHalfPound && (
                        <InputField
                          darkMode={darkMode}
                          type="checkbox"
                          name="halfPound"
                          label="Half Pound Package"
                          value={state.isHalfPound}
                          onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            updateField('isHalfPound', target.checked);
                            updateField('weight', target.checked ? '0.5' : '');
                          }}
                        />
                      )}
                    </div>
                  )}
                  {/* Reordered fields for Sea Shipping: Dimensions, Value, Weight */}
                  {!isWarehouseMode && state.shippingMethod === 'Sea Shipping' && (
                    <div className="grid grid-cols-3 gap-2">
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="length"
                        label="Length (in)"
                        value={state.length}
                        onChange={e => updateField('length', e.target.value)}
                        placeholder="Length"
                        required={areDimensionsRequired}
                        min="0"
                        step="0.1"
                      />
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="width"
                        label="Width (in)"
                        value={state.width}
                        onChange={e => updateField('width', e.target.value)}
                        placeholder="Width"
                        required={areDimensionsRequired}
                        min="0"
                        step="0.1"
                      />
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="height"
                        label="Height (in)"
                        value={state.height}
                        onChange={e => updateField('height', e.target.value)}
                        placeholder="Height"
                        required={areDimensionsRequired}
                        min="0"
                        step="0.1"
                      />
                    </div>
                  )}
                  <div>
                    <InputField
                      darkMode={darkMode}
                      type="number"
                      name="itemValue"
                      label="Item Value (USD)"
                      value={state.itemValue}
                      onChange={e => updateField('itemValue', e.target.value)}
                      placeholder="Enter item value"
                      required
                      min="0"
                      step="0.01"
                      fullWidth
                    />
                  </div>
                  {useTariff && (
                    <TariffCodePicker
                      search={customs.search}
                      categories={customs.categories}
                      value={selectedTariff}
                      onChange={(code) => {
                        setSelectedTariff(code);
                        customs.clear();
                      }}
                    />
                  )}
                  <div>
                    {isWarehouseMode ? (
                      /* Warehouse mode: number input for weight */
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="weight"
                        label="Weight (lbs)"
                        value={state.weight}
                        onChange={e => updateField('weight', e.target.value)}
                        placeholder="Enter package weight"
                        required
                        min="1"
                        max={String(warehouseMaxWeight)}
                        step="1"
                        fullWidth
                      />
                    ) : state.isHalfPound ? (
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="weight"
                        label="Weight (lbs)"
                        value="0.5"
                        onChange={() => {}} // Required by type but unused since disabled
                        disabled
                        fullWidth
                      />
                    ) : (
                      <InputField
                        darkMode={darkMode}
                        type="select"
                        name="weight"
                        label={`Weight (lbs)${isWeightRequired ? ' ' : ' (Optional)'}`}
                        value={state.weight}
                        onChange={e => updateField('weight', e.target.value)}
                        required={isWeightRequired}
                        options={[
                          { value: '', label: 'Select weight' },
                          ...(Array.isArray(state.isExpress ? settings?.expressWeightPrice : settings?.weightPrice)
                            ? (state.isExpress ? settings?.expressWeightPrice : settings?.weightPrice) ?? []
                            : []
                          ).map((_, index) => ({
                            value: (index + 1).toString(),
                            label: `${index + 1} lb`
                          }))
                        ]}
                        fullWidth
                      />
                    )}
                  </div>

                  {/* Insurance Option — not applicable in warehouse mode */}
                  {!isWarehouseMode && (settings?.insuranceFee ?? 0) > 0 && (
                    <InputField
                      darkMode={darkMode}
                      type="checkbox"
                      name="insurance"
                      label={`Add Insurance ($${(settings?.insuranceFee ?? 0).toFixed(2)})`}
                      value={state.insuranceOptIn}
                      onChange={(e) => {
                        const target = e.target as HTMLInputElement;
                        updateField('insuranceOptIn', target.checked);
                      }}
                    />
                  )}
                  {/* Show dimensions for Air Shipping — standard mode only */}
                  {!isWarehouseMode && state.shippingMethod === 'Air Shipping' && (
                    <div className="grid grid-cols-3 gap-2">
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="length"
                        label="Length (in) (Optional)"
                        value={state.length}
                        onChange={e => updateField('length', e.target.value)}
                        placeholder="Length"
                        min="0"
                        step="0.1"
                      />
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="width"
                        label="Width (in) (Optional)"
                        value={state.width}
                        onChange={e => updateField('width', e.target.value)}
                        placeholder="Width"
                        min="0"
                        step="0.1"
                      />
                      <InputField
                        darkMode={darkMode}
                        type="number"
                        name="height"
                        label="Height (in) (Optional)"
                        value={state.height}
                        onChange={e => updateField('height', e.target.value)}
                        placeholder="Height"
                        min="0"
                        step="0.1"
                      />
                    </div>
                  )}
                </>}
              {!isBarrelType && (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!isFormValid()}
                  fullWidth
                >
                  CALCULATE
                </Button>
              )}
            </div>
          </form>
        </div>
        {/* Estimated Costs */}
        {(state.calculated || isBarrelType) && <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 space-y-4 mb-6">
            <h2 className="text-lg font-medium mb-4 dark:text-white">
              {isWarehouseMode ? 'Estimated Warehouse Fees' : 'Estimated Shipping Costs'}
            </h2>
            {isWarehouseMode ? (
              /* Warehouse fee breakdown */
              <>
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="warehousingFee"
                  label="Warehousing Fee ($)"
                  value={formatPrice(state.warehousingFee)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="freightCharge"
                  label="Freight Charge ($)"
                  value={formatPrice(state.freightCharge)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                {parseFloat(state.inspectionFee) > 0 && (
                  <InputField
                    darkMode={darkMode}
                    type="text"
                    name="inspectionFee"
                    label="Inspection Fee ($)"
                    value={formatPrice(state.inspectionFee)}
                    onChange={() => {}}
                    disabled
                    fullWidth
                  />
                )}
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="shippingBagFee"
                  label="Shipping Bag Fee ($)"
                  value={formatPrice(state.shippingBagFee)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="customsEstimate"
                  label="Customs Estimate ($) - Applied for packages $100+"
                  value={formatPrice(state.customsEstimate)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="totalCharges"
                  label="Total Estimated Fees ($)"
                  value={formatPrice(state.totalCharges)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                  className="font-bold"
                />
              </>
            ) : (
              /* Standard fee breakdown */
              <>
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="processingFee"
                  label="Processing Fee ($)"
                  value={formatPrice(state.processingFee)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="freightCharge"
                  label="Freight Charge ($)"
                  value={formatPrice(state.freightCharge)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="customsEstimate"
                  label="Customs Estimate ($) - Applied for packages $100+"
                  value={formatPrice(state.customsEstimate)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                />
                {useTariff && selectedTariff && customs.breakdown && (
                  <CustomsBreakdown estimate={customs.breakdown} formatMoney={formatCustomsMoney} />
                )}
                <InputField
                  darkMode={darkMode}
                  type="text"
                  name="totalCharges"
                  label="Total Charges ($)"
                  value={formatPrice(state.totalCharges)}
                  onChange={() => {}}
                  disabled
                  fullWidth
                  className="font-bold"
                />
              </>
            )}
            <p className="text-sm text-red-500 dark:text-red-400 mt-4">
              Disclaimer: Please note that this is only an estimate.
            </p>
          </div>}
        {/* Callback Request */}
        {!showCustomQuoteForm ? <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="flex items-center mb-4">
              <MessageSquare className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-medium dark:text-white">
                Need Help with Your Shipping?
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Request a callback from our team and we'll help you with your shipping needs.
            </p>
            <Button
              onClick={() => setShowCustomQuoteForm(true)}
              variant="primary"
              color="#f97316" // orange-500
              hoverColor="#f59e0b" // orange-600
            >
              Request a Callback
            </Button>
          </div> : <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 className="text-lg font-medium mb-4 dark:text-white">
              Request a Callback
            </h2>
            {callbackSubmitted ? <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-4 rounded-md mb-4">
                Thank you! We'll contact you shortly via WhatsApp to discuss your shipping needs.
              </div> : <form onSubmit={handleCustomQuoteSubmit}>
                <div className="space-y-4">
                  <InputField
                    darkMode={darkMode}
                    type="phone"
                    name="phoneNumber"
                    label="WhatsApp Number"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    placeholder="876-123-4567"
                    required
                    fullWidth
                  />
                  <div className="flex space-x-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={!phoneNumber || isSubmitting}
                      isLoading={isSubmitting}
                      color="#f97316" // orange-500
                      hoverColor="#f59e0b" // orange-600
                    >
                      Request Callback
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowCustomQuoteForm(false)}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>}
          </div>}
      </div>
    </div>;
};
export default Calculator;
